import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import CirclePolygonAnglesDiagram from "../../../../components/diagrams/CirclePolygonAnglesDiagram";

const DEC_TOL = 0.1;
const CENTRAL_ANGLES = [36, 40, 44, 50, 56, 60, 64, 70, 76, 80, 88, 96, 104, 112, 120];

function rawText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.raw ?? value.value ?? value.input ?? "");
}

function normalize(raw) {
  return String(raw ?? "").trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(raw) {
  return String(raw ?? "").trim().replace(".", "{,}");
}

export function generateTask() {
  const central = CENTRAL_ANGLES[Math.floor(Math.random() * CENTRAL_ANGLES.length)];
  const unknownVertex = Math.random() < 0.5 ? "A" : "B";
  return {
    type: "FindBaseAngleFromRadiiTask",
    data: {
      central,
      unknownVertex,
      answer: (180 - central) / 2,
    },
  };
}

export function renderTask({ data }) {
  const { central, unknownVertex } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      {"Punkty "}<InlineMath math="A" />{" i "}<InlineMath math="B" />{" le\u017c\u0105 na okr\u0119gu o \u015brodku "}<InlineMath math="O" />{". "}
      {"Promienie "}<InlineMath math="OA" />{" i "}<InlineMath math="OB" />{" tworz\u0105 k\u0105t "}<InlineMath math={`${central}^{\\circ}`} />{". "}
      {"Oblicz miar\u0119 k\u0105ta "}<span style={{ whiteSpace: "nowrap" }}><InlineMath math={"\\alpha"} />{" w wierzcho\u0142ku "}<InlineMath math={unknownVertex} />{"."}</span>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram scenario="radii" givenDeg={{ value: central, unknownVertex }} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={rawText(userAnswer)}
      onChange={(value) => onChange({ raw: rawText(value) })}
      disabled={disabled}
      labelLatex={"\\alpha"}
      suffixLatex={"^\\circ"}
      placeholder="np. 52"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return false;
  const value = parseFraction(normalize(raw));
  return Number.isFinite(value) && Math.abs(value - data.answer) <= DEC_TOL;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\alpha=${data.answer}^{\\circ}`} />;
}

export function renderExplanation({ data }) {
  const { central, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <strong>Krok 1.</strong> Odcinki <InlineMath math="OA" /> i <InlineMath math="OB" /> {"s\u0105 promieniami tego samego okr\u0119gu, wi\u0119c maj\u0105 r\u00f3wne d\u0142ugo\u015bci. "}
      {"Tr\u00f3jk\u0105t "}<InlineMath math="AOB" />{" jest r\u00f3wnoramienny."}
      <BlockMath math="OA=OB" />
      <strong>Krok 2.</strong> {"K\u0105ty przy podstawie tego tr\u00f3jk\u0105ta s\u0105 r\u00f3wne."}
      <BlockMath math={`\\alpha=\\frac{180^{\\circ}-${central}^{\\circ}}{2}=${answer}^{\\circ}`} />
      <div style={{ marginTop: 10 }}>
        {"Odpowied\u017a: "}<InlineMath math={`\\alpha=${answer}^{\\circ}`} />{"."}
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\alpha=${pretty(raw)}^{\\circ}`} />;
}

const taskModule = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default taskModule;
